import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Button, Spinner } from 'seagull';
import { IntuitIcon } from 'seagull/icons';
import { getTestIds } from 'seagull/utils/TestIds';
import { redirect, routes } from '~/constants';
import { useOverlayContext } from '~/contexts';
import { useEventReporter } from '~/event-reporter';
import { useUtmsParams } from '~/hooks/useUtmsParams';
import { useLocaleKeys } from '~/i18n';
import { handlePromise } from '~/infra/utils';
import { LoginMethod, useLoginTelemetry } from '~/loginTelemetry/useLoginTelemetry';
import { useOAuthReturnTo } from '~/oauth/useOAuthReturnTo';
import { useOAuthSignIn } from '~/oauth/useOAuthSignIn';
import { useStore, useStoreActions } from '~/store/store-context';
import styles from './IntuitButton.module.scss';
export const GetIntuitButtonTID = (parentTestId)=>getTestIds({
        parentTestId,
        deprecatedTestIds: {
            IntuitButtonTID: {
                Button: 'intuit-sign-in',
                Loading: 'intuit-sign-in-loading'
            }
        }
    });
export function IntuitButton(props) {
    const { mode, ensureEmail, width, returnTo: returnToProp } = props;
    const { api, logBI } = useStoreActions();
    const { t: commonTexts } = useLocaleKeys('common');
    const { showErrorToast } = useOverlayContext();
    const { reportToGtm, reportToAppcues, reportToHubspot, reportToHeap } = useEventReporter();
    const { loginAttempted, loginFailed } = useLoginTelemetry();
    const { pathname } = useLocation();
    const { authRequestParams, webOrigin, signIn: oauthSignIn } = useOAuthSignIn('qbo', mode);
    const { mgmtHost } = useStore((state)=>state.session);
    const getUtms = useUtmsParams();
    const [isLoading, setIsLoading] = useState(false);
    const { IntuitButtonTID } = useMemo(()=>GetIntuitButtonTID(), []);
    const returnTo = useOAuthReturnTo(returnToProp);
    const handleSignIn = async ()=>{
        setIsLoading(true);
        logBI(11802);
        logBI(18904);
        reportToGtm('button-click', 'intuit-sign-in');
        reportToAppcues('button-click', 'intuit-sign-in');
        reportToHubspot('intuit_sign_in');
        reportToHeap('signIn', 'click', 'intuit-sign-in');
        const utms = getUtms();
        const redirectUrl = returnTo ? "".concat(webOrigin).concat(redirect.getPathAndSearch({
            returnTo
        })) : "".concat(webOrigin).concat(routes.home);
        const request = mode === 'signUp' ? api.auth.qboSignUp(_object_spread({
            redirectUrl,
            utms
        }, authRequestParams)) : api.auth.qboSignIn(_object_spread({
            redirectUrl,
            utms,
            ensureEmail: ensureEmail ? {
                email: ensureEmail,
                errorUrl: "".concat(webOrigin).concat(pathname).concat(routes.loginError)
            } : undefined
        }, authRequestParams));
        const result = await handlePromise(request);
        if (result.success && result.data) {
            const intuitUrl = new URL(result.data);
            if (!authRequestParams.native && !IS_PRODUCTION && !IS_CYPRESS) {
                const { protocol } = new URL(webOrigin);
                const redirectUri = new URL("".concat(protocol, "//").concat(mgmtHost, "/qbo-integration/oauth2redirect"));
                redirectUri.searchParams.set('env', 'dev');
                intuitUrl.searchParams.set('redirect_uri', redirectUri.toString());
            }
            const outcome = await loginAttempted(LoginMethod.Intuit, ()=>oauthSignIn(intuitUrl.toString()));
            setIsLoading(outcome === 'redirecting');
        } else {
            loginFailed(LoginMethod.Intuit);
            showErrorToast(commonTexts.toasts.failedToContinueWithIntuit(), 'failed-to-continue-with-intuit-toast');
            setIsLoading(false);
        }
    };
    return /*#__PURE__*/ _jsx(Box, {
        className: styles.buttonContainer,
        children: /*#__PURE__*/ _jsx(Button, {
            testId: IntuitButtonTID.Button(),
            bi: {
                name: 'intuit_signin'
            },
            className: styles.button,
            skin: "primaryOutline",
            onClick: handleSignIn,
            disabled: isLoading,
            prefixIcon: isLoading ? /*#__PURE__*/ _jsx(Spinner, {
                size: 26,
                testId: IntuitButtonTID.Loading()
            }) : /*#__PURE__*/ _jsx(IntuitIcon, {
                size: 36
            }),
            style: width != null ? {
                width
            } : undefined
        })
    });
}
